import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader, Check, X, Users, Calendar, Info, ChevronLeft, ChevronRight, Search, Download, Printer } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/auth.context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function Attendance() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [attendanceFilter, setAttendanceFilter] = useState<string>("all"); // "all", "present", "absent"
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [isMemberDetailsOpen, setIsMemberDetailsOpen] = useState<boolean>(false);
  
  // Initialize with current month
  const getCurrentMonthRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0); // Last day of month
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
      month,
      year
    };
  };

  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [startDate, setStartDate] = useState<string>(getCurrentMonthRange().start);
  const [endDate, setEndDate] = useState<string>(getCurrentMonthRange().end);

  // Update date range when month/year changes
  useEffect(() => {
    const start = new Date(currentYear, currentMonth, 1);
    const end = new Date(currentYear, currentMonth + 1, 0);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, [currentMonth, currentYear]);

  // Month navigation functions
  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    const currentYearNum = new Date().getFullYear();
    const currentMonthNum = new Date().getMonth();
    
    if (currentMonth === 11) {
      const nextYear = currentYear + 1;
      // Prevent navigating to future years
      if (nextYear > currentYearNum) {
        toast.error("Cannot navigate to future years");
        return;
      }
      setCurrentMonth(0);
      setCurrentYear(nextYear);
    } else {
      const nextMonth = currentMonth + 1;
      // Prevent navigating to future months in current year
      if (currentYear === currentYearNum && nextMonth > currentMonthNum) {
        toast.error("Cannot navigate to future months");
        return;
      }
      setCurrentMonth(nextMonth);
    }
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setCurrentMonth(now.getMonth());
    setCurrentYear(now.getFullYear());
  };

  // Generate month and year options
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const currentYearNum = new Date().getFullYear();
  // Only allow years up to current year (no future years)
  const years = Array.from({ length: 6 }, (_, i) => currentYearNum - 5 + i).filter(year => year <= currentYearNum);

  // Fetch groups (already filtered by backend for group leaders)
  const { data: groups = [] } = useQuery(["groups"], async () => {
    const { data } = await api.get("/groups");
    return data.results;
  });

  // Auto-select group for group leaders or when only one group is available
  useEffect(() => {
    if (groups.length > 0 && !selectedGroupId) {
      // Auto-select the first group if:
      // 1. User is not an admin (group leader), OR
      // 2. Only one group is available
      if (!user?.isAdmin || groups.length === 1) {
        setSelectedGroupId(groups[0].id.toString());
      }
    }
  }, [groups, user?.isAdmin, selectedGroupId]);

  // Fetch attendance matrix
  const { data: attendanceMatrix, isLoading, error: attendanceError } = useQuery(
    ["attendance-matrix", selectedGroupId, startDate, endDate],
    async () => {
      if (!selectedGroupId) return null;
      try {
        const { data } = await api.get(`/attendances/matrix/${selectedGroupId}`, {
          params: { startDate, endDate },
        });
        return data.data;
      } catch (error: any) {
        console.error("Error fetching attendance matrix:", error);
        throw error;
      }
    },
    { 
      enabled: !!selectedGroupId,
      retry: 1,
    }
  );

  // Update member attendance mutation
  const updateAttendanceMutation = useMutation(
    async ({ memberId, date, attended }: { memberId: number; date: string; attended: boolean }) => {
      return api.post("/attendances/update-member", {
        groupId: selectedGroupId,
        memberId,
        date,
        attended,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["attendance-matrix"]);
        toast.success(t("pages.attendance.attendanceUpdatedSuccessfully"));
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || error.message);
      },
    }
  );

  const handleToggleAttendance = (memberId: number, date: string, currentStatus: boolean) => {
    // Prevent recording attendance for future dates
    const dateObj = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dateObj.setHours(0, 0, 0, 0);
    
    if (dateObj > today) {
      toast.error("Cannot record attendance for future dates");
      return;
    }
    
    updateAttendanceMutation.mutate({
      memberId,
      date,
      attended: !currentStatus,
    });
  };

  // Set document title
  useEffect(() => {
    document.title = t("pages.attendance.management");
  }, [t]);

  // Check if user is a group leader
  const isGroupLeader = user?.role?.name === "President" || 
                        user?.role?.name === "Accountant" || 
                        user?.role?.name === "Secretary" || 
                        user?.isAdmin;

  // Get selected group details
  const selectedGroup = groups.find(g => g.id.toString() === selectedGroupId);


  // Handle member click to show details
  const handleMemberClick = (memberRow: any) => {
    setSelectedMember(memberRow);
    setIsMemberDetailsOpen(true);
  };

  // Calculate attendance statistics for selected member
  const getMemberStats = (memberRow: any) => {
    if (!memberRow || !memberRow.attendance) {
      return { totalMeetings: 0, attended: 0, absent: 0, attendanceRate: 0 };
    }

    const meetingDays = memberRow.attendance.filter((att: any) => att.isMeetingDay);
    const totalMeetings = meetingDays.length;
    const attended = meetingDays.filter((att: any) => att.attended).length;
    const absent = totalMeetings - attended;
    const attendanceRate = totalMeetings > 0 ? (attended / totalMeetings) * 100 : 0;

    return { totalMeetings, attended, absent, attendanceRate };
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (!attendanceMatrix || !attendanceMatrix.matrix || attendanceMatrix.matrix.length === 0) {
      toast.error("No attendance data to export");
      return;
    }

    try {
      // Prepare data for Excel
      const excelData = filteredMatrix.map((row: any, index: number) => {
        const rowData: any = {
          "#": index + 1,
          "Member": row.member.name,
        };

        // Add attendance for each date
        row.attendance.forEach((att: any) => {
          const dateObj = new Date(att.date);
          const dateKey = dateObj.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
          });
          rowData[dateKey] = att.isMeetingDay 
            ? (att.attended ? "Present" : "Absent")
            : "-";
        });

        return rowData;
      });

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");

      // Generate filename
      const groupName = attendanceMatrix.group?.name || "Group";
      const monthName = months[currentMonth];
      const filename = `Attendance_${groupName}_${monthName}_${currentYear}.xlsx`;

      // Save file
      XLSX.writeFile(workbook, filename);
      toast.success("Attendance data exported to Excel successfully");
    } catch (error: any) {
      toast.error("Failed to export to Excel: " + (error.message || "Unknown error"));
    }
  };

  // Export to PDF
  const handleExportPDF = () => {
    if (!attendanceMatrix || !attendanceMatrix.matrix || attendanceMatrix.matrix.length === 0) {
      toast.error("No attendance data to export");
      return;
    }

    try {
      const doc = new jsPDF('landscape');
      const groupName = attendanceMatrix.group?.name || "Group";
      const monthName = months[currentMonth];

      // Header
      doc.setFontSize(16);
      doc.text(`Attendance Report - ${groupName}`, 14, 10);
      doc.setFontSize(12);
      doc.text(`${monthName} ${currentYear}`, 14, 18);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 24);

      // Prepare table data
      const tableData = filteredMatrix.map((row: any) => {
        const rowData: any[] = [row.member.name];
        
        // Add attendance status for each date
        row.attendance.forEach((att: any) => {
          if (att.isMeetingDay) {
            rowData.push(att.attended ? "✓" : "✗");
          } else {
            rowData.push("-");
          }
        });

        return rowData;
      });

      // Prepare headers
      const headers = ["Member", ...attendanceMatrix.dates.map((date: string) => {
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      })];

      // Add table
      autoTable(doc as any, {
        head: [headers],
        body: tableData,
        startY: 30,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 14, right: 14 },
      });

      // Summary statistics
      const totalMembers = filteredMatrix.length;
      const totalMeetings = attendanceMatrix.dates.filter((date: string) => {
        return attendanceMatrix.matrix[0]?.attendance?.find((att: any) => att.date === date && att.isMeetingDay);
      }).length;

      const finalY = (doc as any).lastAutoTable.finalY || 30;
      doc.setFontSize(10);
      doc.text(`Total Members: ${totalMembers} | Total Meetings: ${totalMeetings}`, 14, finalY + 10);

      // Save file
      const filename = `Attendance_${groupName}_${monthName}_${currentYear}.pdf`;
      doc.save(filename);
      toast.success("Attendance data exported to PDF successfully");
    } catch (error: any) {
      toast.error("Failed to export to PDF: " + (error.message || "Unknown error"));
    }
  };

  // Print attendance report
  const handlePrint = () => {
    if (!attendanceMatrix || !attendanceMatrix.matrix || attendanceMatrix.matrix.length === 0) {
      toast.error("No attendance data to print");
      return;
    }

    try {
      // Create a print-friendly window
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error("Please allow popups to print the report");
        return;
      }

      const groupName = attendanceMatrix.group?.name || "Group";
      const monthName = months[currentMonth];
      
      // Calculate summary statistics
      const totalMembers = filteredMatrix.length;
      const meetingDays = attendanceMatrix.dates.filter((date: string) => {
        return attendanceMatrix.matrix[0]?.attendance?.find((att: any) => att.date === date && att.isMeetingDay);
      });
      const totalMeetings = meetingDays.length;

      let totalAttended = 0;
      let totalAbsent = 0;

      filteredMatrix.forEach((row: any) => {
        row.attendance.forEach((att: any) => {
          if (att.isMeetingDay) {
            if (att.attended) totalAttended++;
            else totalAbsent++;
          }
        });
      });

      const attendanceRate = totalMeetings > 0 
        ? ((totalAttended / (totalMeetings * totalMembers)) * 100).toFixed(1)
        : "0";

      // Build HTML content for printing
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Attendance Report - ${groupName}</title>
          <style>
            @media print {
              @page {
                size: landscape;
                margin: 1cm;
              }
              body {
                margin: 0;
                padding: 0;
              }
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              color: #333;
            }
            .header h2 {
              margin: 5px 0;
              font-size: 18px;
              color: #666;
            }
            .summary {
              display: flex;
              justify-content: space-around;
              margin: 20px 0;
              padding: 15px;
              background-color: #f5f5f5;
              border-radius: 5px;
            }
            .summary-item {
              text-align: center;
            }
            .summary-item strong {
              display: block;
              font-size: 18px;
              color: #333;
            }
            .summary-item span {
              font-size: 14px;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 10px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 6px;
              text-align: left;
            }
            th {
              background-color: #3b82f6;
              color: white;
              font-weight: bold;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9fafb;
            }
            .present {
              color: green;
              font-weight: bold;
              text-align: center;
            }
            .absent {
              color: red;
              font-weight: bold;
              text-align: center;
            }
            .footer {
              margin-top: 20px;
              text-align: center;
              font-size: 12px;
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Attendance Report - ${groupName}</h1>
            <h2>${monthName} ${currentYear}</h2>
            <p>Generated: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="summary">
            <div class="summary-item">
              <strong>${totalMembers}</strong>
              <span>Total Members</span>
            </div>
            <div class="summary-item">
              <strong>${totalMeetings}</strong>
              <span>Total Meetings</span>
            </div>
            <div class="summary-item">
              <strong>${totalAttended}</strong>
              <span>Total Present</span>
            </div>
            <div class="summary-item">
              <strong>${totalAbsent}</strong>
              <span>Total Absent</span>
            </div>
            <div class="summary-item">
              <strong>${attendanceRate}%</strong>
              <span>Attendance Rate</span>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Member</th>
                ${attendanceMatrix.dates.map((date: string) => {
                  const dateObj = new Date(date);
                  return `<th>${dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</th>`;
                }).join('')}
              </tr>
            </thead>
            <tbody>
              ${filteredMatrix.map((row: any, index: number) => {
                return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${row.member.name}</td>
                    ${row.attendance.map((att: any) => {
                      // Show attendance status for all dates, not just meeting days
                      if (att.attended) {
                        return `<td class="present">✓</td>`;
                      } else if (att.isMeetingDay) {
                        return `<td class="absent">✗</td>`;
                      } else {
                        return `<td>-</td>`;
                      }
                    }).join('')}
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Attendance Report generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          </div>
        </body>
        </html>
      `;

      // Write content to print window
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          toast.success("Print dialog opened");
        }, 250);
      };
      
    } catch (error: any) {
      toast.error("Failed to print: " + (error.message || "Unknown error"));
    }
  };

  // Filter attendance matrix based on search and filter criteria
  const filteredMatrix = attendanceMatrix?.matrix ? attendanceMatrix.matrix.filter((row: any) => {
    // Filter by search query (member name)
    const matchesSearch = searchQuery === "" || 
      row.member.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (!matchesSearch) return false;

    // Filter by attendance status
    if (attendanceFilter === "all") return true;
    
    if (attendanceFilter === "present") {
      // Check if member has attended at least one meeting
      return row.attendance.some((att: any) => att.attended && att.isMeetingDay);
    }
    
    if (attendanceFilter === "absent") {
      // Check if member has missed at least one meeting
      return row.attendance.some((att: any) => !att.attended && att.isMeetingDay);
    }
    
    return true;
  }) : [];

  return (
    <div className="container mx-auto py-6 space-y-6 max-w-7xl">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            {t("pages.attendance.management")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track and manage member attendance for group meetings
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold">{t("pages.attendance.selectGroupAndDateRange")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Only show group selector for admins or leaders with multiple groups */}
            {(user?.isAdmin || groups.length > 1) && (
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("pages.attendance.group")}</label>
                <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("pages.attendance.selectGroup")} />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((group) => (
                      <SelectItem key={group.id} value={group.id.toString()}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Show group name as read-only for leaders with single group */}
            {!user?.isAdmin && groups.length === 1 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("pages.attendance.group")}</label>
                <Input
                  value={groups[0]?.name || ""}
                  disabled
                  className="bg-muted"
                />
              </div>
            )}
            {/* Month Navigation */}
            <div className={`space-y-2 ${!(user?.isAdmin || groups.length > 1) && groups.length === 1 ? 'md:col-span-2' : ''}`}>
              <label className="text-sm font-medium">Select Month</label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToPreviousMonth}
                  className="h-10 w-10"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <Select
                    value={currentMonth.toString()}
                    onValueChange={(value) => {
                      const selectedMonth = parseInt(value);
                      const currentYearNum = new Date().getFullYear();
                      const currentMonthNum = new Date().getMonth();
                      // Prevent selecting future months in current year
                      if (currentYear === currentYearNum && selectedMonth > currentMonthNum) {
                        toast.error("Cannot select future months");
                        return;
                      }
                      setCurrentMonth(selectedMonth);
                    }}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month, index) => {
                        const currentYearNum = new Date().getFullYear();
                        const currentMonthNum = new Date().getMonth();
                        const isFutureMonth = currentYear === currentYearNum && index > currentMonthNum;
                        return (
                          <SelectItem 
                            key={index} 
                            value={index.toString()}
                            disabled={isFutureMonth}
                          >
                          {month}
                        </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <Select
                    value={currentYear.toString()}
                    onValueChange={(value) => {
                      const selectedYear = parseInt(value);
                      const currentYearNum = new Date().getFullYear();
                      // Prevent selecting future years
                      if (selectedYear > currentYearNum) {
                        toast.error("Cannot select future years");
                        return;
                      }
                      setCurrentYear(selectedYear);
                    }}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue>{currentYear}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem 
                          key={year} 
                          value={year.toString()}
                          disabled={year > new Date().getFullYear()}
                        >
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToNextMonth}
                  className="h-10 w-10"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToCurrentMonth}
                  className="h-10 px-3"
                >
                  Today
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Viewing: {months[currentMonth]} {currentYear}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Group Meeting Info */}
      {selectedGroup && attendanceMatrix?.group && (
        <Card className="border shadow-sm bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Group</p>
                  <p className="text-base font-semibold text-gray-900">{attendanceMatrix.group.name}</p>
              </div>
              {attendanceMatrix.group.meetingFrequency && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Frequency</p>
                    <p className="text-base font-semibold text-gray-900">{attendanceMatrix.group.meetingFrequency}</p>
                </div>
              )}
              {attendanceMatrix.group.meetingDay && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Meeting Day</p>
                    <p className="text-base font-semibold text-gray-900">{attendanceMatrix.group.meetingDay}</p>
                </div>
              )}
              {attendanceMatrix.dates && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Meetings</p>
                    <p className="text-base font-semibold text-gray-900">{attendanceMatrix.dates.length} scheduled</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attendance Matrix */}
      {selectedGroupId && (
        <Card className="border shadow-sm">
          <CardHeader className="border-b bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <CardTitle className="text-xl font-semibold">
                {t("pages.attendance.attendanceMatrix")}
              </CardTitle>
              <div className="flex items-center gap-2">
              {attendanceMatrix?.dates && attendanceMatrix.dates.length > 0 && (
                <Badge variant="outline" className="text-sm">
                  {attendanceMatrix.dates.length} {attendanceMatrix.dates.length === 1 ? 'meeting' : 'meetings'}
                </Badge>
              )}
                {/* Download and Share Buttons */}
                {attendanceMatrix && attendanceMatrix.matrix && attendanceMatrix.matrix.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportPDF}
                      className="h-8"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportExcel}
                      className="h-8"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Excel
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrint}
                      className="h-8"
                    >
                      <Printer className="h-4 w-4 mr-1" />
                      Print
                    </Button>
            </div>
                )}
              </div>
            </div>
            {/* Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search members by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="w-full sm:w-[200px]">
                <Select value={attendanceFilter} onValueChange={setAttendanceFilter}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Members</SelectItem>
                    <SelectItem value="present">Present (Attended)</SelectItem>
                    <SelectItem value="absent">Absent (Missed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Results count */}
            {attendanceMatrix?.matrix && (
              <div className="mt-2 text-sm text-muted-foreground">
                Showing {filteredMatrix.length} of {attendanceMatrix.matrix.length} members
                {searchQuery && ` matching "${searchQuery}"`}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader className="h-8 w-8 animate-spin" />
              </div>
            ) : attendanceError ? (
              <div className="text-center py-8">
                <div className="text-red-600 font-medium mb-2">
                  {t("pages.attendance.errorLoadingAttendance")}
                </div>
                {attendanceError instanceof Error && (
                  <div className="text-sm text-muted-foreground">
                    {attendanceError.message}
                  </div>
                )}
              </div>
            ) : attendanceMatrix ? (
              filteredMatrix && filteredMatrix.length > 0 ? (
                <div className="p-4">
                  <div 
                    className="w-full overflow-x-auto border rounded-lg shadow-sm"
                    style={{ scrollbarWidth: 'thin' }}
                  >
                    <Table className="min-w-max">
                        <TableHeader>
                          <TableRow className="bg-gray-50 hover:bg-gray-50">
                          <TableHead className="sticky left-0 bg-blue-50 z-20 min-w-[40px] max-w-[40px] font-semibold text-gray-700 border-r text-center shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                              #
                            </TableHead>
                          <TableHead className="sticky left-[40px] bg-blue-50 z-20 min-w-[180px] max-w-[180px] font-semibold text-gray-700 border-r shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                              {t("pages.attendance.member")}
                            </TableHead>
                            {attendanceMatrix.dates.map((date: string) => {
                              const dateObj = new Date(date);
                              const isToday = dateObj.toDateString() === new Date().toDateString();
                              // Check if this is a meeting day by looking at the first row's attendance data
                              const isMeetingDay = attendanceMatrix.matrix[0]?.attendance?.find((att: any) => att.date === date)?.isMeetingDay || false;
                            const dayNumber = dateObj.getDate();
                            const weekday = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                              return (
                                <TableHead 
                                  key={date} 
                                className={`text-center min-w-[60px] max-w-[60px] px-1 font-semibold text-gray-700 ${
                                  isToday ? 'bg-blue-100 border-2 border-blue-400' : ''
                                } ${isMeetingDay ? 'bg-green-50 border-l border-r border-green-400' : 'border-l border-gray-200'}`}
                                title={`${dateObj.toLocaleDateString('en-US', { 
                                  weekday: 'long',
                                  month: 'long', 
                                  day: 'numeric',
                                  year: 'numeric'
                                })}${isMeetingDay ? ' - Meeting Day' : ''}`}
                                >
                                <div className="flex flex-col items-center justify-center gap-0.5 py-1">
                                  <span className={`text-[10px] uppercase leading-tight ${isMeetingDay ? 'text-green-700 font-bold' : 'text-gray-500'}`}>
                                    {weekday}
                                    </span>
                                  <span className={`text-xs font-semibold leading-tight ${isMeetingDay ? 'text-green-700' : isToday ? 'text-blue-700' : 'text-gray-700'}`}>
                                    {dayNumber}
                                    </span>
                                    {isMeetingDay && (
                                    <span className="text-[8px] text-green-600 font-medium leading-tight">M</span>
                                    )}
                                  </div>
                                </TableHead>
                              );
                            })}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                        {filteredMatrix.map((row: any, index: number) => {
                          // Find the original index for numbering
                          const originalIndex = attendanceMatrix.matrix.findIndex((r: any) => r.member.id === row.member.id);
                          const rowBg = index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50';
                          return (
                            <TableRow 
                              key={row.member.id} 
                            className={rowBg}
                            >
                            <TableCell className={`sticky left-0 bg-blue-50 z-10 font-medium border-r text-center shadow-[2px_0_4px_rgba(0,0,0,0.1)]`}>
                              <span className="text-xs font-semibold text-gray-600">
                                {originalIndex >= 0 ? originalIndex + 1 : index + 1}
                                </span>
                              </TableCell>
                            <TableCell 
                              className={`sticky left-[40px] bg-blue-50 z-10 font-medium border-r shadow-[2px_0_4px_rgba(0,0,0,0.1)] cursor-pointer hover:bg-blue-100 transition-colors`}
                              onClick={() => handleMemberClick(row)}
                            >
                              <span className="text-sm font-semibold text-gray-900 truncate block">
                                  {row.member.name}
                                </span>
                              </TableCell>
                              {row.attendance.map((att: any) => {
                                const dateObj = new Date(att.date);
                                const isToday = dateObj.toDateString() === new Date().toDateString();
                                const isMeetingDay = att.isMeetingDay || false;
                                return (
                                  <TableCell 
                                    key={att.date} 
                                  className={`text-center px-1 py-2 ${
                                    isToday ? 'bg-blue-100 border-l border-r border-blue-400' : ''
                                    } ${
                                    isMeetingDay ? 'bg-green-50 border-l border-r border-green-400' : 'border-l border-gray-200'
                                    }`}
                                  >
                                    {isGroupLeader ? (
                                      <div className="flex items-center justify-center">
                                        <Checkbox
                                          checked={att.attended}
                                          onCheckedChange={() =>
                                            handleToggleAttendance(row.member.id, att.date, att.attended)
                                          }
                                          disabled={updateAttendanceMutation.isLoading || (() => {
                                            const dateObj = new Date(att.date);
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            dateObj.setHours(0, 0, 0, 0);
                                            return dateObj > today;
                                          })()}
                                        className="h-4 w-4 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                                        />
                                      </div>
                                    ) : (
                                      <div className="flex items-center justify-center">
                                        {att.attended ? (
                                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100">
                                          <Check className="h-3 w-3 text-green-600" />
                                        </div>
                                      ) : isMeetingDay ? (
                                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-100">
                                          <X className="h-3 w-3 text-red-600" />
                                          </div>
                                        ) : (
                                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100">
                                          <span className="text-[8px] text-gray-400">-</span>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                        </TableBody>
                      </Table>
                    </div>
                  {/* Scroll indicator */}
                  <div className="mt-2 text-xs text-muted-foreground text-center">
                    <span className="inline-flex items-center gap-1">
                      <ChevronLeft className="h-3 w-3" />
                      Scroll horizontally to view all dates
                      <ChevronRight className="h-3 w-3" />
                    </span>
                  </div>
                </div>
              ) : attendanceMatrix.matrix && attendanceMatrix.matrix.length > 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery || attendanceFilter !== "all" 
                    ? "No members match your search or filter criteria"
                    : t("pages.attendance.noMembersInGroup") || "No members found in this group"}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {t("pages.attendance.noMembersInGroup") || "No members found in this group"}
                </div>
              )
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {t("pages.attendance.selectGroupToViewAttendance")}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!selectedGroupId && (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              {t("pages.attendance.selectGroupToViewAttendance")}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Member Attendance Details Modal */}
      <Dialog open={isMemberDetailsOpen} onOpenChange={setIsMemberDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="border-b px-6 py-4 flex-shrink-0">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              {selectedMember?.member?.name || "Member"} - Attendance Details
            </DialogTitle>
            <DialogDescription>
              View detailed attendance records and statistics
            </DialogDescription>
          </DialogHeader>

          {selectedMember && (() => {
            const stats = getMemberStats(selectedMember);
            const meetingDays = selectedMember.attendance?.filter((att: any) => att.isMeetingDay) || [];
            
            return (
              <ScrollArea className="flex-1 px-6 py-4">
                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <Card className="border shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Calendar className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Total Meetings</p>
                          <p className="text-lg font-semibold text-gray-900">{stats.totalMeetings}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Check className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Attended</p>
                          <p className="text-lg font-semibold text-green-700">{stats.attended}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                          <X className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Absent</p>
                          <p className="text-lg font-semibold text-red-700">{stats.absent}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border shadow-sm">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Info className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-600">Attendance Rate</p>
                          <p className="text-lg font-semibold text-purple-700">
                            {stats.attendanceRate.toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Attendance Records Table */}
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Meeting Attendance Records</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold">Date</TableHead>
                          <TableHead className="font-semibold">Day</TableHead>
                          <TableHead className="font-semibold text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {meetingDays.length > 0 ? (
                          meetingDays.map((att: any, idx: number) => {
                            const dateObj = new Date(att.date);
                            return (
                              <TableRow key={att.date} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                <TableCell className="font-medium">
                                  {dateObj.toLocaleDateString('en-US', {
                                    month: 'long',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </TableCell>
                                <TableCell>
                                  {dateObj.toLocaleDateString('en-US', { weekday: 'long' })}
                                </TableCell>
                                <TableCell className="text-center">
                                  {att.attended ? (
                                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                      <Check className="h-3 w-3 mr-1" />
                                      Present
                                    </Badge>
                                  ) : (
                                    <Badge variant="destructive" className="bg-red-100 text-red-700 hover:bg-red-100">
                                      <X className="h-3 w-3 mr-1" />
                                      Absent
                                    </Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground py-4">
                              No meeting records found for this period
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </ScrollArea>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
